import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "convex/react";
import { api } from "../lib/api";
import { useAccess, setDemoStaffPreference } from "../lib/access";
import { caughtErrorMessage } from "../domain/errors";
import { isClerkEnabled } from "../lib/clerkEnv";
import { useCompany } from "../lib/company";
import {
  demoStaffRedirectRoute,
  demoStaffToggleBusy,
  demoStaffToggleError,
  demoStaffToggleView,
  emptyDemoStaffToggleState,
  nextDemoStaffToggleState,
} from "../domain/users";
import { Button } from "./ui";

export function DemoStaffToggle() {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, company } = useCompany();
  const { isStaff } = useAccess();
  const setDemoStaffMode = useMutation(api.users.setDemoStaffMode);
  const [toggleState, setToggleState] = useState(() => emptyDemoStaffToggleState());

  if (!isClerkEnabled) return null;
  if (company?.clerkOrgId !== "demo:lawstream") return null;

  const busy = demoStaffToggleBusy(toggleState);
  const error = demoStaffToggleError(toggleState);
  const view = demoStaffToggleView({ isStaff, busy, hasCompany: companyId !== null });

  const toggle = async () => {
    if (companyId === null || busy) return;
    const next = !isStaff;
    setToggleState((current) => nextDemoStaffToggleState(current, { type: "started", enabled: next }));
    try {
      await setDemoStaffMode({ companyId, enabled: next });
      setDemoStaffPreference(next);
      setToggleState((current) => nextDemoStaffToggleState(current, { type: "succeeded" }));
      const redirect = demoStaffRedirectRoute({ enabled: next, pathname: location.pathname });
      if (redirect === "/alerts") {
        window.setTimeout(() => navigate(redirect), 0);
      } else if (redirect !== null) {
        navigate(redirect);
      }
    } catch (caught) {
      setToggleState((current) => nextDemoStaffToggleState(current, {
        type: "failed",
        message: caughtErrorMessage(caught, "Could not switch demo staff mode."),
      }));
    }
  };

  return (
    <span className="relative inline-flex">
      <Button
        size="sm"
        variant={view.variant}
        icon="shield"
        disabled={view.disabled}
        onClick={toggle}
        aria-pressed={view.ariaPressed}
        title={error ?? view.title}
      >
        {view.label}
      </Button>
      {error !== null && (
        <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500" title={error} />
      )}
    </span>
  );
}
